<template>
  <div class="settings-design">

    <div class="card" style="margin-bottom:16px;">
      <div class="card-header">
        <div>
          <div class="card-title"><svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>Accounting software</div>
          <div class="card-subtitle">Connect your Xero account to push monthly figures as draft invoices</div>
        </div>
      </div>
      <div class="card-body">
        <div style="font-size:12px;color:var(--text-muted);margin-bottom:14px;line-height:1.6;">Connect your accounting software to push locked monthly reports as draft invoices. More integrations coming soon.</div>

        <div style="display:flex;flex-direction:column;gap:8px;margin-bottom:16px;">

          <!-- Xero connection card (wired) -->
          <div :style="xeroConnected
            ? 'border:1px solid var(--green-border);border-radius:8px;overflow:hidden;background:var(--green-dim);'
            : 'border:1px solid var(--border);border-radius:8px;overflow:hidden;'">
            <div style="padding:12px 16px;display:flex;align-items:center;justify-content:space-between;">
              <div style="display:flex;align-items:center;gap:10px;">
                <div :style="xeroConnected
                  ? 'width:32px;height:32px;background:var(--green);border-radius:8px;display:flex;align-items:center;justify-content:center;flex-shrink:0;'
                  : 'width:32px;height:32px;background:#13B5EA;border-radius:8px;display:flex;align-items:center;justify-content:center;flex-shrink:0;color:white;font-family:Outfit,sans-serif;font-weight:800;font-size:13px;'">
                  <svg v-if="xeroConnected" viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="white" stroke-width="2.2"><polyline points="2,8 6,12 14,4"/></svg>
                  <span v-else>X</span>
                </div>
                <div>
                  <div :style="xeroConnected
                    ? 'font-size:13px;font-weight:600;color:var(--green);'
                    : 'font-size:13px;font-weight:600;color:var(--text);'">
                    Xero — {{ xeroConnected ? 'Connected' : 'Not connected' }}
                  </div>
                  <div style="font-size:11px;color:var(--text-muted);margin-top:1px;">
                    <span v-if="xeroConnected">{{ xeroData.tenant_name }}<span v-if="xeroData.connected_at"> · Connected {{ formatDate(xeroData.connected_at) }}</span></span>
                    <span v-else>Connect your Xero account to push reports</span>
                  </div>
                </div>
              </div>
              <button
                v-if="xeroConnected"
                class="btn btn-outline btn-sm"
                :disabled="disconnecting"
                @click="disconnectXero"
              >{{ disconnecting ? 'Disconnecting…' : 'Disconnect' }}</button>
              <button
                v-else
                class="btn btn-primary btn-sm"
                :disabled="connecting"
                @click="connectXero"
              >{{ connecting ? 'Opening…' : 'Connect' }}</button>
            </div>
          </div>

          <!-- QuickBooks (mockup as-is, not wired) -->
          <div style="border:1px solid var(--border);border-radius:8px;overflow:hidden;">
            <div style="padding:12px 16px;display:flex;align-items:center;justify-content:space-between;">
              <div style="display:flex;align-items:center;gap:10px;">
                <div style="width:32px;height:32px;background:#2CA01C;border-radius:8px;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
                  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="white" stroke-width="2"><line x1="8" y1="3" x2="8" y2="9"/><circle cx="8" cy="12" r="1" fill="white" stroke="none"/></svg>
                </div>
                <div>
                  <div style="font-size:13px;font-weight:600;color:var(--text);display:flex;align-items:center;gap:6px;">QuickBooks — Not connected <span class="future-tag">future</span></div>
                  <div style="font-size:11px;color:var(--text-muted);margin-top:1px;">Connect your QuickBooks account to push reports</div>
                </div>
              </div>
              <button class="btn btn-primary btn-sm" disabled>Connect</button>
            </div>
          </div>

        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-header">
        <div>
          <div class="card-title"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>What gets sent to your accounting software</div>
          <div class="card-subtitle">Example draft invoice — Mayfair Rose — May 2026</div>
        </div>
      </div>
      <div class="card-body">

        <div style="background:var(--white);border:1px solid var(--border);border-radius:var(--radius);overflow:hidden;margin-bottom:16px;">
          <div style="padding:10px 16px;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;background:var(--bg);flex-wrap:wrap;gap:8px;">
            <div style="font-size:12px;font-weight:600;color:var(--text);">Example draft invoice — Mayfair Rose — May 2026</div>
            <div style="font-size:11px;color:var(--text-muted);">This is how it arrives in your accounting software</div>
          </div>
          <div style="padding:12px 16px;border-bottom:1px solid var(--border);display:grid;grid-template-columns:repeat(5,1fr);gap:12px;">
            <div><div style="font-size:10px;color:var(--text-muted);margin-bottom:3px;">Contact</div><div style="font-size:12px;font-weight:600;color:var(--text);">Mr James Hargreaves</div></div>
            <div><div style="font-size:10px;color:var(--text-muted);margin-bottom:3px;">Reference</div><div style="font-size:12px;color:var(--text);">Mayfair Rose - 05/2026</div></div>
            <div><div style="font-size:10px;color:var(--text-muted);margin-bottom:3px;">Date / Due</div><div style="font-size:12px;color:var(--text);">1 May 2026 → 15 May 2026</div></div>
            <div><div style="font-size:10px;color:var(--text-muted);margin-bottom:3px;">Currency</div><div style="font-size:12px;color:var(--text);">GBP</div></div>
            <div><div style="font-size:10px;color:var(--text-muted);margin-bottom:3px;">Amounts are</div><div style="font-size:12px;color:var(--text);">Tax exclusive</div></div>
          </div>
          <div style="overflow-x:auto;">
            <table style="width:100%;border-collapse:collapse;font-size:12px;min-width:680px;">
              <thead>
                <tr style="background:var(--bg);border-bottom:1px solid var(--border);">
                  <th style="text-align:left;padding:8px 12px;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-muted);">Description</th>
                  <th style="text-align:right;padding:8px 12px;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-muted);">Qty</th>
                  <th style="text-align:right;padding:8px 12px;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-muted);">Price</th>
                  <th style="text-align:left;padding:8px 12px;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-muted);">Account</th>
                  <th style="text-align:left;padding:8px 12px;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-muted);">Tax rate</th>
                  <th style="text-align:right;padding:8px 12px;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-muted);">Tax amount</th>
                  <th style="text-align:right;padding:8px 12px;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-muted);">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr style="border-bottom:1px solid var(--border);">
                  <td style="padding:10px 12px;color:var(--text);">Management Fees</td>
                  <td style="padding:10px 12px;text-align:right;color:var(--text);">1</td>
                  <td style="padding:10px 12px;text-align:right;color:var(--text);">991.44</td>
                  <td style="padding:10px 12px;color:var(--text);">200 - Sales</td>
                  <td style="padding:10px 12px;color:var(--text);">20% Output VAT</td>
                  <td style="padding:10px 12px;text-align:right;color:var(--text);">198.29</td>
                  <td style="padding:10px 12px;text-align:right;color:var(--text);">991.44</td>
                </tr>
                <tr style="border-bottom:1px solid var(--border);">
                  <td style="padding:10px 12px;color:var(--text);">Laundry &amp; Linen Supply</td>
                  <td style="padding:10px 12px;text-align:right;color:var(--text);">1</td>
                  <td style="padding:10px 12px;text-align:right;color:var(--text);">230.00</td>
                  <td style="padding:10px 12px;color:var(--text);">200 - Sales</td>
                  <td style="padding:10px 12px;color:var(--text);">20% Output VAT</td>
                  <td style="padding:10px 12px;text-align:right;color:var(--text);">46.00</td>
                  <td style="padding:10px 12px;text-align:right;color:var(--text);">230.00</td>
                </tr>
                <tr style="border-bottom:1px solid var(--border);">
                  <td style="padding:10px 12px;color:var(--text);">Consumables &amp; Guest Supplies</td>
                  <td style="padding:10px 12px;text-align:right;color:var(--text);">1</td>
                  <td style="padding:10px 12px;text-align:right;color:var(--text);">25.00</td>
                  <td style="padding:10px 12px;color:var(--text);">200 - Sales</td>
                  <td style="padding:10px 12px;color:var(--text);">20% Output VAT</td>
                  <td style="padding:10px 12px;text-align:right;color:var(--text);">5.00</td>
                  <td style="padding:10px 12px;text-align:right;color:var(--text);">25.00</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style="padding:12px 16px;display:flex;justify-content:flex-end;border-top:1px solid var(--border);">
            <div style="min-width:200px;">
              <div style="display:flex;justify-content:space-between;padding:4px 0;font-size:12px;border-bottom:1px solid var(--border);"><span style="color:var(--text-muted);">Subtotal</span><span style="color:var(--text);">£1,246.44</span></div>
              <div style="display:flex;justify-content:space-between;padding:4px 0;font-size:12px;border-bottom:1px solid var(--border);"><span style="color:var(--text-muted);">Total VAT</span><span style="color:var(--text);">£249.29</span></div>
              <div style="display:flex;justify-content:space-between;padding:7px 0 2px;font-size:14px;font-weight:600;"><span style="color:var(--text);">Total</span><span style="color:var(--text);">£1,495.73</span></div>
            </div>
          </div>
          <div style="padding:8px 16px;background:var(--bg);border-top:1px solid var(--border);display:flex;align-items:center;gap:7px;">
            <div style="width:7px;height:7px;border-radius:50%;background:#f59e0b;flex-shrink:0;"></div>
            <div style="font-size:11px;color:var(--text-muted);">Draft · only supplied services with VAT applied are included · disbursements are excluded</div>
          </div>
        </div>

        <div style="display:flex;flex-direction:column;gap:8px;">
          <div style="display:flex;align-items:flex-start;gap:10px;font-size:12px;color:var(--text-muted);line-height:1.6;">
            <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="var(--green)" stroke-width="2" style="flex-shrink:0;margin-top:2px;"><polyline points="2,8 6,12 14,4"/></svg>
            <span><strong style="color:var(--text);">Only supplied services with VAT applied are included.</strong> Disbursements are excluded — these are costs passed to the owner, not your income.</span>
          </div>
          <div style="display:flex;align-items:flex-start;gap:10px;font-size:12px;color:var(--text-muted);line-height:1.6;">
            <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="var(--green)" stroke-width="2" style="flex-shrink:0;margin-top:2px;"><polyline points="2,8 6,12 14,4"/></svg>
            <span><strong style="color:var(--text);">Lines are in fixed order:</strong> Management Fees · Laundry &amp; Linen Supply · Consumables &amp; Guest Supplies. Description shows only the service — property name and period appear in the invoice header.</span>
          </div>
          <div style="display:flex;align-items:flex-start;gap:10px;font-size:12px;color:var(--text-muted);line-height:1.6;">
            <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="var(--green)" stroke-width="2" style="flex-shrink:0;margin-top:2px;"><polyline points="2,8 6,12 14,4"/></svg>
            <span><strong style="color:var(--text);">Always a draft.</strong> Nothing is posted until your bookkeeper approves it.</span>
          </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AccountingTab',
  data() {
    return {
      xeroConnected: false,
      xeroData: {},
      connecting: false,
      disconnecting: false
    };
  },
  computed: {
    baseUrl() {
      return process.env.VUE_APP_API_URL || '';
    }
  },
  methods: {
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    async fetchXeroStatus() {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${this.baseUrl}/api/pms/xero-status/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.data.success && response.data.data.connected) {
          this.xeroConnected = true;
          this.xeroData = response.data.data;
        } else {
          this.xeroConnected = false;
          this.xeroData = {};
        }
      } catch (e) {
        console.error('Failed to fetch Xero status:', e);
      }
    },
    async connectXero() {
      this.connecting = true;
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${this.baseUrl}/api/pms/xero-connect/`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.data.success) {
          window.location.href = response.data.data.authorization_url;
        }
      } catch (e) {
        console.error('Error connecting to Xero:', e);
        alert(e.response?.data?.message || 'Failed to connect to Xero');
      } finally {
        this.connecting = false;
      }
    },
    async disconnectXero() {
      if (!confirm('Are you sure you want to disconnect Xero?')) return;
      this.disconnecting = true;
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${this.baseUrl}/api/pms/xero-disconnect/`, {}, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        this.xeroConnected = false;
        this.xeroData = {};
      } catch (e) {
        console.error('Error disconnecting Xero:', e);
        alert(e.response?.data?.message || 'Failed to disconnect');
      } finally {
        this.disconnecting = false;
      }
    }
  },
  mounted() {
    this.fetchXeroStatus();
  }
};
</script>

<style scoped>
.settings-design {
  --accent: #5b4fcf;
  --accent-dim: rgba(91,79,207,0.08);
  --accent-border: rgba(91,79,207,0.25);
  --bg: #f5f4fb;
  --white: #ffffff;
  --text: #1a1a2e;
  --text-mid: #4a4880;
  --text-muted: #9896bb;
  --border: #e8e6f4;
  --green: #00b894;
  --green-dim: rgba(0,184,148,0.1);
  --green-border: rgba(0,184,148,0.25);
  --red: #d63031;
  --red-dim: rgba(214,48,49,0.08);
  --red-border: rgba(214,48,49,0.2);
  --gold: #f59e0b;
  --gold-dim: rgba(245,158,11,0.1);
  --gold-border: rgba(245,158,11,0.3);
  --shadow-sm: 0 1px 4px rgba(26,24,61,0.07);
  --radius: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: var(--text);
  -webkit-font-smoothing: antialiased;
}
.settings-design *, .settings-design *::before, .settings-design *::after { box-sizing: border-box; }

/* CARDS */
.card { background: var(--white); border-radius: var(--radius); border: 1px solid var(--border); box-shadow: var(--shadow-sm); overflow: hidden; margin-bottom: 18px; }
.card-header { padding: 16px 22px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; }
.card-title { font-family: 'Outfit', sans-serif; font-size: 0.9rem; font-weight: 700; color: var(--text); display: flex; align-items: center; gap: 8px; }
.card-title svg { width: 15px; height: 15px; fill: none; stroke: var(--accent); stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.card-subtitle { font-size: 12px; color: var(--text-muted); margin-top: 2px; }
.card-body { padding: 20px 22px; }

/* BUTTONS */
.btn { display: inline-flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; border: none; font-family: 'Inter', sans-serif; transition: opacity 0.15s, transform 0.1s; white-space: nowrap; }
.btn:hover { opacity: 0.88; }
.btn:active { transform: scale(0.98); }
.btn:disabled { opacity: 0.6; cursor: default; }
.btn:disabled:hover { opacity: 0.6; }
.btn-primary { background: var(--accent); color: #fff; }
.btn-outline { background: transparent; color: var(--text-mid); border: 1.5px solid var(--border); }
.btn-sm { padding: 5px 11px; font-size: 12px; }

.future-tag {
  font-size: 10px;
  font-weight: 600;
  color: #b7791f;
  background: rgba(253,203,110,0.2);
  border: 1px solid rgba(253,203,110,0.5);
  padding: 1px 7px;
  border-radius: 20px;
}
</style>
